import {z} from 'zod';

import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  ConfigureTelephonyLineRoutingRequestBody,
  TelephonyFallbackKindSchema,
  TelephonyLineRouting,
  TelephonyRoutingTargetKindSchema,
} from '@dms/api/settings';
import i18n from '@dms/i18n';

export const ROUTING_MESSAGE_MAX_LENGTH = 1000;

const routingMessageSchema = z.string().trim().min(1).max(ROUTING_MESSAGE_MAX_LENGTH);

export const telephonyLineRoutingFormSchema = z
  .object({
    openTargetKind: TelephonyRoutingTargetKindSchema,
    groupId: z.string().nullable(),
    userId: z.string().nullable(),
    closedKind: TelephonyFallbackKindSchema,
    closedMessage: routingMessageSchema,
    noAnswerKind: TelephonyFallbackKindSchema,
    noAnswerMessage: routingMessageSchema,
  })
  .superRefine((values, ctx) => {
    if (values.openTargetKind === 'group' && isNilOrEmpty(values.groupId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('general.validations.fieldIsRequired'),
        path: ['groupId'],
      });
    }

    if (values.openTargetKind === 'user' && isNilOrEmpty(values.userId)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t('general.validations.fieldIsRequired'),
        path: ['userId'],
      });
    }
  });

export type TelephonyLineRoutingFormValues = z.infer<typeof telephonyLineRoutingFormSchema>;

/**
 * The default rule carries no target and no texts, so customizing starts from a group target with
 * the out-of-hours greeting as the first draft of both messages.
 */
export const getTelephonyLineRoutingFormValues = (
  routing: TelephonyLineRouting,
  greetingOutOfHours: string | null
): TelephonyLineRoutingFormValues => {
  const openTarget = routing.isDefault ? null : routing.openTarget;

  return {
    openTargetKind: openTarget?.kind ?? 'group',
    groupId: openTarget?.kind === 'group' ? openTarget.id : null,
    userId: openTarget?.kind === 'user' ? openTarget.id : null,
    closedKind: routing.closedFallback?.kind ?? 'message',
    closedMessage: routing.closedFallback?.message ?? greetingOutOfHours ?? '',
    noAnswerKind: routing.noAnswerFallback?.kind ?? 'message',
    noAnswerMessage: routing.noAnswerFallback?.message ?? greetingOutOfHours ?? '',
  };
};

export const getConfigureTelephonyLineRoutingRequestBody = (
  values: TelephonyLineRoutingFormValues
): ConfigureTelephonyLineRoutingRequestBody | null => {
  const targetId = values.openTargetKind === 'group' ? values.groupId : values.userId;

  if (isNil(targetId)) {
    return null;
  }

  return {
    openTarget: {kind: values.openTargetKind, id: targetId},
    closedFallback: {kind: values.closedKind, message: values.closedMessage},
    noAnswerFallback: {kind: values.noAnswerKind, message: values.noAnswerMessage},
  };
};
